import { useEffect } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { TransportCommand } from '../transport/TransportCommand';
import { isIOSNativeApp } from '../utils/platform';

/**
 * When the iOS user selects a new Bluetooth/AirPlay output via the system
 * route picker, this hook detects the route change and — if a network
 * renderer is currently active — stops the renderer and switches to local
 * playback so audio plays through the newly selected device.
 */
export function useIOSRouteSwitch() {
  useEffect(() => {
    if (!isIOSNativeApp()) return;

    const handler = async (e) => {
      const store = useMaestroStore.getState();
      const renderer = store.activeConnectorRenderer;
      if (!renderer) return;

      const routeName = e.detail?.name || 'Speaker';
      console.log('[useIOSRouteSwitch] Route changed to', routeName, '— switching from renderer', renderer.name, 'to local');

      // 1. Stop the network renderer
      try {
        await TransportCommand.stopRenderer(renderer.id, { caller: 'ios_route_switch', userInitiated: true });
      } catch (err) {
        console.error('[useIOSRouteSwitch] stopRenderer failed:', err);
      }

      // 2. Switch store to local zone
      store.setActiveConnectorRenderer(null);
      const zones = store.discovery?.discoveredZones || [];
      const localZone = zones.find(z =>
        z.type === 'local' || z.type === 'browser' ||
        z.id === 'this_computer' || z.id === 'local_computer' || z.id === 'this_device'
      );
      if (localZone) {
        store.setActiveZone(localZone);
        store.setZoneName(localZone.name);
      }

      // 3. Resume local playback if a track was playing
      const track = store.currentTrack;
      const url = store.currentStreamUrl || store.alternateStreamUrl;
      if (track && url && store.playbackState !== 'stopped') {
        try {
          const { getClientTransport } = await import('../handlers/ClientTransportHandler');
          const ct = getClientTransport();
          ct.playLocalStream({ ...track, streamUrl: url });
          console.log('[useIOSRouteSwitch] Resumed local playback');
        } catch (err) {
          console.error('[useIOSRouteSwitch] playLocalStream failed:', err);
        }
      }
    };

    window.addEventListener('maestroAudioRouteChanged', handler);
    return () => window.removeEventListener('maestroAudioRouteChanged', handler);
  }, []);
}
